import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../config/api';
import { apiFetch, isLoggedIn } from '../utils/session';
import './AreaHeadSosListener.css';

function requestNotificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission === 'default') {
    Notification.requestPermission().catch(() => {});
  }
}

export default function AreaHeadSosListener() {
  const [banner, setBanner] = useState(null);
  const seenRef = useRef(null);
  const initialRef = useRef(true);

  useEffect(() => {
    if (banner) {
      document.body.classList.add('has-sos-banner');
    } else {
      document.body.classList.remove('has-sos-banner');
    }
    return () => document.body.classList.remove('has-sos-banner');
  }, [banner]);

  useEffect(() => {
    if (!isLoggedIn()) return undefined;

    const role = sessionStorage.getItem('sangramUserRole');
    if (role !== 'Area Head' && role !== 'Admin') return undefined;

    requestNotificationPermission();

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await apiFetch(apiUrl('/sos/active'));
        const data = await res.json().catch(() => ({}));
        if (!res.ok || cancelled) return;

        const alerts = data.alerts || [];
        const ids = new Set(alerts.map((a) => String(a._id)));

        if (initialRef.current) {
          initialRef.current = false;
          seenRef.current = ids;
          return;
        }

        const prev = seenRef.current || new Set();
        let bannerPayload = null;
        for (const a of alerts) {
          const id = String(a._id);
          if (!prev.has(id)) {
            const name = a.userId?.name || 'Member';
            const area = a.area || '';
            if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
              try {
                new Notification('Sangram Sena — SOS', {
                  body: `${name} — ${area}`,
                  tag: `sos-${id}`,
                });
              } catch {
                /* ignore */
              }
            }
            if (!bannerPayload) {
              bannerPayload = {
                id,
                title: 'Emergency SOS',
                body: `${name} in ${area} needs help.`,
              };
            }
          }
        }
        if (bannerPayload) setBanner(bannerPayload);
        seenRef.current = ids;
      } catch {
        /* ignore network errors */
      }
    };

    poll();
    const interval = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  if (!banner) return null;

  return (
    <div className="sos-banner" role="alert">
      <div className="sos-banner-text">
        <strong>{banner.title}</strong>
        <span>{banner.body}</span>
      </div>
      <div className="sos-banner-actions">
        <Link className="sos-banner-link" to="/area-head/sos" onClick={() => setBanner(null)}>
          Open alerts
        </Link>
        <button type="button" className="sos-banner-dismiss" onClick={() => setBanner(null)}>
          Dismiss
        </button>
      </div>
    </div>
  );
}
